import { useOrganization } from "@clerk/clerk-react";
import { useState, useMemo, useEffect } from "react";
import { View, Pressable } from "react-native";
import { usePermissionsService } from "../../../services/api/permissions-service";
import AgentMulti from "./agent-multi";
import { useMemberPermissions } from "../../../hooks/useMemberPermissions";
import { useQueryClient } from "@tanstack/react-query";
import { useAgents } from "../../../hooks/useAgents";
import StyledText from "../../common/ui/styled-text";
import { Loader } from "lucide-react-native";

const Access = () => {
  const queryClient = useQueryClient();
  const { saveMemberPermissions } = usePermissionsService();
  const { memberships } = useOrganization({
    memberships: {
      keepPreviousData: true,
    },
  });
  const { data: agents } = useAgents();
  const agentOptions = useMemo(() => {
    return (
      agents?.map((agent) => ({ label: agent.name, value: agent.id })) ?? []
    );
  }, [agents]);

  const members = useMemo(
    () =>
      memberships?.data
        ?.filter((membership) => membership.role === "org:member")
        .filter((membership) => membership.publicUserData.userId?.length),
    [memberships?.data]
  );
  const memberIds = useMemo(
    () => members?.map((membership) => membership.publicUserData.userId!),
    [members]
  );

  const { data: permissions } = useMemberPermissions(memberIds);

  const [userPermissions, setUserPermissions] = useState<
    Record<string, string[]>
  >(permissions ?? {});

  useEffect(() => {
    setUserPermissions(permissions ?? {});
  }, [permissions]);

  const handlePermissionChange = (userId: string, locationIds: string[]) => {
    setUserPermissions((prev) => ({
      ...prev,
      [userId]: locationIds,
    }));
  };

  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const handleSave = async () => {
    try {
      setIsLoadingSave(true);
      setError(null);
      setSuccess(null);
      await Promise.all(
        Object.entries(userPermissions).map(([userId, locationIds]) =>
          saveMemberPermissions(userId, locationIds)
        )
      );
      // Invalidate queries for all affected users
      Object.keys(userPermissions).forEach((userId) => {
        queryClient.invalidateQueries({
          queryKey: ["member-permissions", userId],
        });
      });
      setSuccess("Settings saved successfully.");
    } catch (error) {
      console.error("Error saving permissions:", error);
      setError("There was an error saving your settings.");
    } finally {
      setIsLoadingSave(false);
    }
  };

  if (!memberIds || memberIds?.length === 0) {
    return (
      <View className="flex flex-col items-center justify-center h-full">
        <StyledText className="text-2xl font-bold">
          Access Management
        </StyledText>
        <StyledText>This organization has no members.</StyledText>
      </View>
    );
  }

  return (
    <View>
      <View className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-6">
        <StyledText className="text-2xl font-bold">
          Access Management
        </StyledText>
        <Pressable
          onPress={handleSave}
          disabled={isLoadingSave}
          className={`flex-row items-center justify-center gap-2 mr-4 rounded-md px-4 py-2 bg-primary ${
            isLoadingSave ? "opacity-70" : ""
          }`}
        >
          {isLoadingSave && (
            <Loader
              size={16}
              color="white"
              className="animate-spin [transform-origin:center]"
            />
          )}
          <StyledText className="text-white font-medium">
            {isLoadingSave ? "Saving..." : "Save"}
          </StyledText>
        </Pressable>
      </View>
      {error && (
        <StyledText className="font-medium text-red-500 my-2">
          {error}
        </StyledText>
      )}
      {success && (
        <StyledText className="font-medium text-green-500 my-2">
          {success}
        </StyledText>
      )}
      <View className="rounded-md border mr-4">
        <View className="hidden sm:block">
          <View className="w-full">
            <View className="border-b">
              <View className="flex-row">
                <View className="p-4 font-medium text-left w-1/3">
                  <StyledText>User Email</StyledText>
                </View>
                <View className="p-4 font-medium text-left w-2/3">
                  <StyledText>Location Access</StyledText>
                </View>
              </View>
            </View>
            <View>
              {members?.map((member) => {
                return (
                  <View
                    key={member.id}
                    className="flex-row border-b last:border-b-0"
                  >
                    <View className="p-4 text-left w-1/3">
                      <StyledText>
                        {member.publicUserData.identifier.slice(0, 22)}
                        {member.publicUserData.identifier.length > 22 && "..."}
                      </StyledText>
                    </View>
                    <View className="p-4 text-left w-2/3">
                      <AgentMulti
                        agentOptions={agentOptions}
                        userId={member.publicUserData.userId!}
                        locationIds={
                          userPermissions[member.publicUserData.userId!] || []
                        }
                        onChange={handlePermissionChange}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </View>

        <View className="block sm:hidden space-y-4">
          {members?.map((member) => {
            const userId = member.publicUserData.userId!;
            return (
              <View key={member.id} className="space-y-3 p-4">
                <StyledText className="text-base font-medium">
                  {member.publicUserData.identifier}
                </StyledText>
                <AgentMulti
                  agentOptions={agentOptions}
                  userId={userId}
                  locationIds={userPermissions[userId] || []}
                  onChange={handlePermissionChange}
                />
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
};

export default Access;
